import { lazy, Suspense } from "react";
import {
    Navigate,
    Route,
    BrowserRouter as Router,
    Routes,
    useParams,
} from "react-router-dom";
import { LocalizationUrlSync } from "../components/LocalizationUrlSync";
import { buildMessagesPath } from "../helpers/Messages/chatHelper";
import { ResourcesProvider } from "../context/Messages/ResourcesContext";
import { SigninEmail } from "../components/Auth/SigninEmail";
import { VerifyEmailOtp } from "../components/Auth/VerifyEmailOtp";
import ClassSessions from "../components/ClassSessions/ClassSessions";
import { Home } from "../components/Home/Home";
import FooterOnlyLayout from "../components/Layout/FooterOnlyLayout";
import FullLayout from "../components/Layout/FullLayout";
import { LiveSession } from "../components/LiveSession/LiveSession";
import SessionReport from "../components/SessionReport/SessionReport";
import { SessionDetailsPage } from "../components/SessionDetails/SessionDetailsPage";
import Admin from "../components/Admin/Admin";
import Profile from "../components/Profile/Profile";
import ManageClasses from "../components/Admin/ManageClasses/ManageClasses";
import ClassDetail from "../components/Admin/ManageClasses/ClassDetail/ClassDetail";
import Earnings from "../components/Admin/Earnings/Earnings";
import MonthEarnings from "../components/Admin/Earnings/MonthEarnings/MonthEarnings";
import { PrivateRoute } from "../guards/PrivateRoute";
import { useLocalization } from "../stores/localizationContext";

const MessagesChatContent = lazy(() => import("../components/Messages/ChatContent"));

function RedirectToLang() {
    const { lang } = useLocalization();
    return <Navigate to={`/${lang}`} replace />;
}

function RedirectLegacyMessages() {
    const { lang } = useLocalization();
    const { chatId } = useParams<{ chatId?: string }>();
    return <Navigate to={buildMessagesPath(lang, chatId)} replace />;
}

const messagesRouteElement = (
    <PrivateRoute>
        <ResourcesProvider>
            <Suspense fallback={<div className="page chat-page" />}>
                <MessagesChatContent />
            </Suspense>
        </ResourcesProvider>
    </PrivateRoute>
);

const AppRoutes = () => {
    return (
        <Router>
            <LocalizationUrlSync />
            <Routes>
                {/* Public Routes - Must come BEFORE parametized routes */}
                <Route element={<FooterOnlyLayout />}>
                    <Route path="/:language/signinemail" element={<SigninEmail />} />
                    <Route path="/:language/verifyemailotp" element={<VerifyEmailOtp />} />
                </Route>
                {/* Private Routes */}
                {/* Redirect bare root to /{lang} */}
                <Route path="/" element={<RedirectToLang />} />

                <Route element={<FullLayout />}>
                    <Route path="/messages" element={<RedirectLegacyMessages />} />
                    <Route path="/messages/:chatId" element={<RedirectLegacyMessages />} />
                    <Route path="/:language/messages" element={messagesRouteElement} />
                    <Route path="/:language/messages/:chatId" element={messagesRouteElement} />
                    <Route
                        path="/:language"
                        element={
                            <PrivateRoute>
                                <Home />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/classes/:classId/sessions"
                        element={
                            <PrivateRoute>
                                <ClassSessions />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/sessions/:sessionId/report"
                        element={
                            <PrivateRoute>
                                <SessionReport />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/classes/:classId/sessions/:sessionId"
                        element={
                            <PrivateRoute>
                                <SessionDetailsPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/admin"
                        element={
                            <PrivateRoute>
                                <Admin />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/profile"
                        element={
                            <PrivateRoute>
                                <Profile />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/admin/classes"
                        element={
                            <PrivateRoute>
                                <ManageClasses />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/admin/classes/:classId"
                        element={
                            <PrivateRoute>
                                <ClassDetail />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/admin/earnings"
                        element={
                            <PrivateRoute>
                                <Earnings />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/:language/admin/earnings/:year/:month"
                        element={
                            <PrivateRoute>
                                <MonthEarnings />
                            </PrivateRoute>
                        }
                    />
                </Route>
                <Route
                    path="/:language/sessions/:sessionId/live/:materialId"
                    element={
                        <PrivateRoute>
                            <LiveSession />
                        </PrivateRoute>
                    }
                />
                {/* Fallback - redirect authenticated users to home, others to signin */}
                <Route
                    path="*"
                    element={
                        <PrivateRoute>
                            <Navigate to="/" replace />
                        </PrivateRoute>
                    }
                />
            </Routes>
        </Router>
    );
};

export default AppRoutes;
